import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/forms/Field";
import { Star, Trash2 } from "lucide-react";
import { getFavorite, isFavorite, removeFavorite, saveFavorite } from "@/lib/favorites";
import { notifySaved } from "@/lib/notifySaved";

export function FavoriteModal({
  open,
  onClose,
  url,
  defaultLabel,
  clientName,
}: {
  open: boolean;
  onClose: () => void;
  url: string;
  defaultLabel?: string;
  clientName?: string;
}) {
  const [name, setName] = useState("");
  const [pinned, setPinned] = useState(false);
  const starred = isFavorite(url);

  useEffect(() => {
    if (open) {
      const fav = getFavorite(url);
      setName(fav?.label || defaultLabel || "");
      setPinned(Boolean(fav?.pinnedToDashboard));
    }
  }, [open, url, defaultLabel]);

  const handleSave = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const finalName = name.trim() || defaultLabel || "Favorite View";
    saveFavorite(url, finalName, {
      ...(clientName ? { client: clientName } : {}),
      pinnedToDashboard: pinned,
    });
    notifySaved(starred ? "Favorite view updated" : "Saved view to favorites");
    onClose();
  };

  const handleRemove = () => {
    removeFavorite(url);
    notifySaved("Removed from favorites");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={starred ? "Edit Favorite View" : "Favorite Task View"}
      footer={
        <div className="flex items-center justify-between w-full">
          {starred ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
              className="inline-flex items-center gap-1.5"
            >
              <Trash2 size={13} aria-hidden="true" /> Remove
            </Button>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={() => handleSave()} className="inline-flex items-center gap-1.5">
              <Star size={13} className="fill-amber-400 text-amber-400" aria-hidden="true" />
              {starred ? "Save changes" : "Save to favorites"}
            </Button>
          </div>
        </div>
      }
    >
      <form onSubmit={handleSave} className="space-y-4 py-1">
        <Field label="View name" hint="Give this custom task view a clear, memorable name.">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. My Sprint Backlog, Bug Triage"
            autoFocus
          />
        </Field>
        <label className="flex items-center gap-2 text-xs font-medium text-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={pinned}
            onChange={(e) => setPinned(e.target.checked)}
            className="rounded border-border text-primary focus:ring-primary h-3.5 w-3.5"
          />
          <span>Pin this view to Dashboard</span>
        </label>
        <div className="rounded-md bg-secondary/50 p-2.5 text-xs text-muted-foreground break-all">
          <span className="font-semibold text-foreground/80">View URL:</span> {url}
        </div>
      </form>
    </Modal>
  );
}
